/**
 * Cart Drawer Component - JavaScript
 * Handles drawer open/close, quantity updates, item removal, and cart syncing
 * ============================================
 * 
 * Usage:
 * 1. Add data-cart-toggle to any element to toggle the drawer
 * 2. Dispatch 'cart:open' or 'cart:close' events on window
 * 
 * Events:
 *   window.dispatchEvent(new CustomEvent('cart:open'));
 *   window.dispatchEvent(new CustomEvent('cart:close'));
 */

(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.__cartDrawerInitialized) return;
  window.__cartDrawerInitialized = true;

  document.addEventListener('DOMContentLoaded', initCartDrawer);

  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const closeBtn = drawer.querySelector('[data-cart-close]');
    const overlay = drawer.querySelector('[data-cart-overlay]');
    const body = drawer.querySelector('[data-cart-drawer-body]');
    const footer = drawer.querySelector('[data-cart-drawer-footer]');
    let itemsContainer = drawer.querySelector('[data-cart-drawer-items]');
    const emptyState = drawer.querySelector('[data-cart-drawer-empty]');
    const countEl = drawer.querySelector('[data-cart-drawer-count]');
    const subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
    const notesToggle = drawer.querySelector('[data-notes-toggle]');
    const notesTextarea = drawer.querySelector('[data-cart-note]');
    const offersCarousel = drawer.querySelector('[data-cart-offers-carousel]');
    
    // Check if auto-open on add is enabled
    const section = drawer.closest('.cart-drawer-section');
    const openOnAdd = section ? section.querySelector('[data-cart-drawer]') !== null : true;

    let notesSaveTimeout = null;
    
    // Track keys currently being removed to prevent duplicate API calls
    const removingKeys = new Set();

    /**
     * Open the cart drawer
     */
    function openDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');

      // Update all toggle buttons
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.setAttribute('aria-expanded', 'true');
      });

      // Focus on close button
      setTimeout(() => {
        if (closeBtn) closeBtn.focus();
      }, 100);

      // GSAP animation for cart items - use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
      if (typeof gsap !== 'undefined' && itemsContainer) {
        const items = itemsContainer.querySelectorAll('[data-cart-drawer-item]');
        
          if (items.length > 0) {
        gsap.fromTo(items,
          { opacity: 0, x: 30 },
          { 
            opacity: 1, 
            x: 0, 
            duration: 0.4, 
            stagger: 0.05,
            ease: 'power3.out',
            delay: 0.1,
            onComplete: () => {
              // Initialize marquee for long titles after animation
              initTitleMarquees();
            }
          }
        );
          } else {
            // No items, still init marquees (in case they appear)
            setTimeout(initTitleMarquees, 200);
          }
      } else {
        // Initialize marquee even without GSAP
        setTimeout(initTitleMarquees, 200);
      }

      // Initialize offers carousel
      initOffersCarousel();

      // Re-initialize Lucide icons
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
      });

      // Dispatch opened event
      window.dispatchEvent(new CustomEvent('cart:opened'));
    }

    /**
     * Close the cart drawer
     */
    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');

      // Update all toggle buttons
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.setAttribute('aria-expanded', 'false');
      });

      // Dispatch closed event
      window.dispatchEvent(new CustomEvent('cart:closed'));
    }

    /**
     * Toggle the drawer state
     */
    function toggleDrawer() {
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      if (isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    }

    /**
     * Update quantity for a cart item
     */
    async function updateQuantity(key, newQuantity) {
      try {
        if (window.CartAPI) {
          await window.CartAPI.updateItem(key, newQuantity);
        } else {
          // Fallback
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQuantity })
          });
          
          if (!response.ok) throw new Error('Failed to update cart');
          
          const cart = await response.json();
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        }
      } catch (error) {
        console.error('Failed to update quantity:', error);
      }
    }

    /**
     * Remove item from cart
     */
    async function removeItem(key) {
      // Check if this key is already being removed (prevents race condition after re-render)
      if (removingKeys.has(key)) {
        return;
      }
      
      const itemEl = drawer.querySelector(`[data-line-key="${key}"]`);
      
      // Prevent double-clicks - check if already being removed (DOM-level check as backup)
      if (!itemEl || itemEl.classList.contains('is-removing')) {
        return;
      }
      
      // Mark as being removed at component level (survives DOM re-renders)
      removingKeys.add(key);
      
      // Mark as being removed at DOM level
      itemEl.classList.add('is-removing');
      
      // Disable the remove button
      const removeBtn = itemEl.querySelector('[data-remove-item]');
      if (removeBtn) removeBtn.disabled = true;
      
      try {
        // Call API first, before animation
        if (window.CartAPI) {
          await window.CartAPI.removeItem(key);
        } else {
          await updateQuantity(key, 0);
        }
        
        // API succeeded - now animate the removal
        if (typeof gsap !== 'undefined') {
          const tl = gsap.timeline({
            onComplete: () => {
              // Remove element after animation
              itemEl.remove();
            }
          });
          
          // Phase 1: Fade out with slight scale
          tl.to(itemEl, {
            autoAlpha: 0,
            scale: 0.95,
            duration: 0.2,
            ease: 'power2.out'
          });
          
          // Phase 2: Collapse height smoothly
          tl.to(itemEl, {
            height: 0,
            marginBottom: 0,
            paddingTop: 0,
            paddingBottom: 0,
            duration: 0.25,
            ease: 'power2.inOut',
            onStart: () => {
              itemEl.style.overflow = 'hidden';
            }
          }, '-=0.05');
        } else {
          // No GSAP - just remove immediately
          itemEl.remove();
        }
        
      } catch (error) {
        console.error('Failed to remove item:', error);
        // Reset state on error
        removingKeys.delete(key);
        itemEl.classList.remove('is-removing');
        if (removeBtn) removeBtn.disabled = false;
      } finally {
        // Clean up the key from the set after animation completes (give it time)
        setTimeout(() => removingKeys.delete(key), 500);
      }
    }

    /**
     * Save order note
     */
    async function saveNote(note) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note })
        });
      } catch (error) {
        console.error('Failed to save note:', error);
      }
    }

    /**
     * Re-render cart drawer with new data
     */
    async function refreshCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update count
        if (countEl) {
          countEl.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;
        }

        // Update subtotal with compare price if discounts apply
        if (subtotalEl) {
          if (cart.original_total_price > cart.total_price) {
            subtotalEl.innerHTML = `
              <span class="cart-drawer__subtotal-current">${formatMoney(cart.total_price)}</span>
              <span class="cart-drawer__subtotal-compare">${formatMoney(cart.original_total_price)}</span>
            `;
          } else {
            subtotalEl.textContent = formatMoney(cart.total_price);
          }
        }

        // Update header cart count
        const headerCartCount = document.querySelector('[data-cart-count]');
        if (headerCartCount) {
          headerCartCount.textContent = cart.item_count;
          if (cart.item_count > 0) {
            headerCartCount.classList.add('is-visible');
          } else {
            headerCartCount.classList.remove('is-visible');
          }
        }

        // Handle empty state
        if (cart.item_count === 0) {
          if (itemsContainer) itemsContainer.style.display = 'none';
          if (emptyState) emptyState.style.display = 'flex';
          if (footer) footer.style.display = 'none';
        } else {
          if (emptyState) emptyState.style.display = 'none';
          if (footer) footer.style.display = 'flex';
          
          // Create items container if it doesn't exist (fallback for when cart was initially empty)
          if (!itemsContainer && body) {
            itemsContainer = document.createElement('div');
            itemsContainer.className = 'cart-drawer__items';
            itemsContainer.setAttribute('data-cart-drawer-items', '');
            // Insert at the beginning of body, before empty state
            body.insertBefore(itemsContainer, body.firstChild);
          }
          
          // Re-render items
          if (itemsContainer) {
            itemsContainer.style.display = 'flex';
            renderCartItems(cart.items);
          }
        }

        // Update progress bar
        updateProgressBar(cart.total_price);

        // Update discounts section
        updateDiscounts(cart);

        // Re-initialize Lucide icons
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }

      } catch (error) {
        console.error('Failed to refresh cart:', error);
      }
    }

    /**
     * Update discounts section
     */
    function updateDiscounts(cart) {
      const discountsContainer = drawer.querySelector('[data-cart-drawer-discounts]');
      const footerEl = drawer.querySelector('[data-cart-drawer-footer]');
      
      // Get cart-level discounts
      const discounts = cart.cart_level_discount_applications || [];
      
      if (discounts.length === 0) {
        // Remove discounts section if no discounts
        if (discountsContainer) {
          discountsContainer.remove();
        }
        return;
      }
      
      // Build discounts HTML
      const discountsHTML = discounts.map(discount => `
        <div class="cart-drawer__discount">
          <span class="cart-drawer__discount-icon"><span data-lucide="badge-percent"></span></span>
          <span class="cart-drawer__discount-title">${escapeHtml(discount.title)}</span>
          <span class="cart-drawer__discount-amount">-${formatMoney(discount.total_allocated_amount)}</span>
        </div>
      `).join('');
      
      if (discountsContainer) {
        // Update existing container
        discountsContainer.innerHTML = discountsHTML;
      } else if (footerEl) {
        // Create new container before subtotal
        const newDiscountsContainer = document.createElement('div');
        newDiscountsContainer.className = 'cart-drawer__discounts';
        newDiscountsContainer.setAttribute('data-cart-drawer-discounts', '');
        newDiscountsContainer.innerHTML = discountsHTML;
        
        // Insert before subtotal
        const subtotalEl = footerEl.querySelector('.cart-drawer__subtotal');
        if (subtotalEl) {
          subtotalEl.parentNode.insertBefore(newDiscountsContainer, subtotalEl);
        } else {
          footerEl.appendChild(newDiscountsContainer);
        }
      }
    }

    /**
     * Build display title with variant
     */
    function buildDisplayTitle(item) {
      let title = item.product_title;
      if (item.variant_title && item.variant_title !== 'Default Title') {
        // Replace " / " with "/" for compact display
        const variantDisplay = item.variant_title.replace(/ \/ /g, '/');
        title = `${title} - ${variantDisplay}`;
      }
      return title;
    }

    /**
     * Render cart items HTML
     */
    function renderCartItems(items) {
      if (!itemsContainer) return;

      itemsContainer.innerHTML = items.map((item, index) => {
        const displayTitle = buildDisplayTitle(item);
        
        return `
        <div 
          class="cart-drawer__item" 
          data-cart-drawer-item 
          data-line-key="${item.key}"
          data-variant-id="${item.variant_id}"
          style="--item-index: ${index}"
        >
          <a href="${item.url}" class="cart-drawer__item-media">
            ${item.image ? `
              <img 
                src="${getSizedImageUrl(item.image, '160x')}" 
                alt="${escapeHtml(item.title)}"
                width="80"
                height="80"
                loading="lazy"
                class="cart-drawer__item-image"
              >
            ` : `
              <div class="cart-drawer__item-placeholder">
                <span data-lucide="package"></span>
              </div>
            `}
          </a>

          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title-wrapper" data-title-wrapper>
              <span class="cart-drawer__item-title" data-title>
                <span class="cart-drawer__item-title-text">${escapeHtml(displayTitle)}</span>
                <span class="cart-drawer__item-title-text" aria-hidden="true">${escapeHtml(displayTitle)}</span>
              </span>
            </a>

            <div class="cart-drawer__item-price">
              ${item.original_line_price !== item.final_line_price ? `
                <span class="cart-drawer__item-price--sale">${formatMoney(item.final_line_price)}</span>
                <span class="cart-drawer__item-price--compare">${formatMoney(item.original_line_price)}</span>
              ` : (item.original_price > item.final_price ? `
                <span class="cart-drawer__item-price--sale">${formatMoney(item.final_line_price)}</span>
                <span class="cart-drawer__item-price--compare">${formatMoney(item.original_price * item.quantity)}</span>
              ` : `
                <span>${formatMoney(item.final_line_price)}</span>
              `)}
            </div>

            ${item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0 ? `
              <div class="cart-drawer__item-discounts">
                ${item.line_level_discount_allocations.map(discount => `
                  <span class="cart-drawer__item-discount">
                    ${escapeHtml(discount.discount_application.title)} (-${formatMoney(discount.amount)})
                  </span>
                `).join('')}
              </div>
            ` : ''}

            <div class="cart-drawer__item-quantity">
              <button 
                type="button" 
                class="cart-drawer__qty-btn" 
                data-qty-decrease
                data-line-key="${item.key}"
                aria-label="Decrease quantity"
              >
                <span data-lucide="minus"></span>
              </button>
              <span class="cart-drawer__qty-value" data-qty-value>${item.quantity}</span>
              <button 
                type="button" 
                class="cart-drawer__qty-btn" 
                data-qty-increase
                data-line-key="${item.key}"
                aria-label="Increase quantity"
              >
                <span data-lucide="plus"></span>
              </button>
            </div>
          </div>

          <button 
            type="button" 
            class="cart-drawer__item-remove" 
            data-remove-item
            data-line-key="${item.key}"
            aria-label="Remove ${escapeHtml(item.title)}"
          >
            <span data-lucide="trash-2"></span>
          </button>
        </div>
      `}).join('');

      // Re-attach event listeners
      attachItemListeners();
      
      // Initialize marquees after render
      setTimeout(initTitleMarquees, 50);
    }

    /**
     * Attach event listeners to cart items
     */
    function attachItemListeners() {
      // Quantity decrease
      drawer.querySelectorAll('[data-qty-decrease]').forEach(btn => {
        btn.addEventListener('click', async function() {
          const key = this.dataset.lineKey;
          const itemEl = this.closest('[data-cart-drawer-item]');
          const valueEl = this.parentElement.querySelector('[data-qty-value]');
          const currentQty = parseInt(valueEl.textContent) || 1;
          const newQty = Math.max(1, currentQty - 1);
          
          if (newQty !== currentQty) {
            // Show loading state
            if (itemEl) itemEl.classList.add('is-loading');
            valueEl.textContent = newQty;
            
            await updateQuantity(key, newQty);
            
            // Remove loading state (will be re-rendered anyway)
            if (itemEl) itemEl.classList.remove('is-loading');
          }
        });
      });

      // Quantity increase
      drawer.querySelectorAll('[data-qty-increase]').forEach(btn => {
        btn.addEventListener('click', async function() {
          const key = this.dataset.lineKey;
          const itemEl = this.closest('[data-cart-drawer-item]');
          const valueEl = this.parentElement.querySelector('[data-qty-value]');
          const currentQty = parseInt(valueEl.textContent) || 1;
          const newQty = currentQty + 1;
          
          // Show loading state
          if (itemEl) itemEl.classList.add('is-loading');
          valueEl.textContent = newQty;
          
          await updateQuantity(key, newQty);
          
          // Remove loading state (will be re-rendered anyway)
          if (itemEl) itemEl.classList.remove('is-loading');
        });
      });

      // Remove item
      drawer.querySelectorAll('[data-remove-item]').forEach(btn => {
        btn.addEventListener('click', function() {
          const key = this.dataset.lineKey;
          removeItem(key);
        });
      });
    }

    /**
     * Initialize marquee animation for titles that overflow
     */
    function initTitleMarquees() {
      const titleWrappers = drawer.querySelectorAll('[data-title-wrapper]');
      
      titleWrappers.forEach(wrapper => {
        const titleEl = wrapper.querySelector('[data-title]');
        if (!wrapper || !titleEl) return;
        
        // Kill any existing animation
        if (wrapper._marqueeAnim) {
          wrapper._marqueeAnim.kill();
          wrapper._marqueeAnim = null;
        }
        
        // Reset state
        wrapper.classList.remove('is-marquee');
        gsap.set(titleEl, { x: 0 });
        
        // Check if title overflows container
        const wrapperWidth = wrapper.offsetWidth;
        const textEl = titleEl.querySelector('.cart-drawer__item-title-text');
        const textWidth = textEl ? textEl.offsetWidth : titleEl.scrollWidth;
        
        if (textWidth > wrapperWidth) {
          // Enable marquee mode
          wrapper.classList.add('is-marquee');
          
          // Calculate animation distance (text width + gap)
          const distance = textWidth;
          const duration = Math.max(5, distance / 40); // Speed: 40px per second, min 5s
          
          // Create infinite scrolling animation
          wrapper._marqueeAnim = gsap.to(titleEl, {
            x: -distance,
            duration: duration,
            ease: 'none',
            repeat: -1,
            repeatDelay: 1
          });
        }
      });
    }

    /**
     * Update spending goal progress bar
     */
    function updateProgressBar(cartTotal) {
      const progressEl = drawer.querySelector('[data-cart-drawer-progress]');
      if (!progressEl) return;

      const threshold = parseInt(progressEl.dataset.threshold) || 0;
      const goalName = progressEl.dataset.goalName || 'Free Shipping';
      const progressText = progressEl.dataset.progressText || 'Spend [amount] more to unlock [goal]!';
      const successText = progressEl.dataset.successText || 'You\'ve unlocked [goal]!';

      const remaining = Math.max(0, threshold - cartTotal);
      const progress = Math.min(100, (cartTotal / threshold) * 100);

      // Update progress fill
      const fillEl = progressEl.querySelector('[data-progress-fill]');
      if (fillEl) {
        fillEl.style.width = `${progress}%`;
      }

      // Update message
      const textEl = progressEl.querySelector('.cart-drawer__progress-text');
      const messageEl = progressEl.querySelector('[data-progress-message]');

      if (remaining > 0) {
        textEl.classList.remove('cart-drawer__progress-text--success');
        const formattedAmount = formatMoney(remaining);
        messageEl.textContent = progressText.replace('[amount]', formattedAmount).replace('[goal]', goalName);
        
        // Update icon to truck
        const iconEl = textEl.querySelector('[data-lucide]');
        if (iconEl) {
          iconEl.setAttribute('data-lucide', 'truck');
          if (typeof lucide !== 'undefined') lucide.createIcons();
        }
      } else {
        textEl.classList.add('cart-drawer__progress-text--success');
        messageEl.textContent = successText.replace('[goal]', goalName);
        
        // Update icon to check-circle
        const iconEl = textEl.querySelector('[data-lucide]');
        if (iconEl) {
          iconEl.setAttribute('data-lucide', 'check-circle');
          if (typeof lucide !== 'undefined') lucide.createIcons();
        }
      }
    }

    /**
     * Initialize offers carousel
     */
    function initOffersCarousel() {
      if (!offersCarousel) return;

      const offers = window.themeOffers || [];
      const placeholder = offersCarousel.querySelector('[data-cart-offers-placeholder]');

      if (offers.length === 0) {
        if (placeholder) {
          placeholder.querySelector('.cart-drawer__offers-title').textContent = 'No offers available';
        }
        return;
      }

      // Hide placeholder
      if (placeholder) {
        placeholder.classList.add('is-hidden');
        setTimeout(() => placeholder.remove(), 400);
      }

      // Check if offers already rendered
      if (offersCarousel.querySelectorAll('.cart-drawer__offers-item:not([data-cart-offers-placeholder])').length > 0) {
        return;
      }

      // Create offer elements
      offers.forEach((offer, index) => {
        const offerEl = document.createElement('div');
        offerEl.className = 'cart-drawer__offers-item';
        offerEl.dataset.offerIndex = index;
        
        offerEl.innerHTML = `
          <span class="cart-drawer__offers-title">
            ${offer.title || 'Special Offer'}
            ${offer.badge ? `<span class="cart-drawer__offers-badge">${offer.badge}</span>` : ''}
          </span>
          <span class="cart-drawer__offers-code">Use code: ${offer.code}</span>
        `;
        
        offersCarousel.appendChild(offerEl);
      });

      const offerEls = offersCarousel.querySelectorAll('.cart-drawer__offers-item:not([data-cart-offers-placeholder])');
      let currentIndex = 0;
      let cycleInterval = null;

      function showOffer(index) {
        offerEls.forEach((el, i) => {
          if (i === index) {
            el.classList.remove('is-exiting');
            el.classList.add('is-active');
          } else if (el.classList.contains('is-active')) {
            el.classList.add('is-exiting');
            el.classList.remove('is-active');
          } else {
            el.classList.remove('is-active', 'is-exiting');
          }
        });
      }

      function nextOffer() {
        currentIndex = (currentIndex + 1) % offers.length;
        showOffer(currentIndex);
      }

      // Show first offer
      showOffer(0);

      // Only cycle if more than one offer
      if (offers.length > 1) {
        cycleInterval = setInterval(nextOffer, 4000);

        // Pause on hover
        const offersBlock = drawer.querySelector('[data-cart-drawer-offers]');
        if (offersBlock) {
          offersBlock.addEventListener('mouseenter', () => {
            clearInterval(cycleInterval);
          });

          offersBlock.addEventListener('mouseleave', () => {
            cycleInterval = setInterval(nextOffer, 4000);
          });
        }
      }
    }

    /**
     * Format money using store's currency format
     */
    function formatMoney(cents) {
      // Use theme money format
      const format = window.themeMoneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      const valueNoDecimals = Math.round(cents / 100).toString();
      const valueWithComma = value.replace('.', ',');
      const valueNoDecimalsWithSpaces = valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const valueWithSpaces = value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      
      // Handle all Shopify money format placeholders
      let result = format;
      
      // With decimals
      result = result.replace(/\{\{\s*amount\s*\}\}/g, value);
      result = result.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, valueWithComma);
      result = result.replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, value.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
      result = result.replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, valueWithSpaces);
      
      // Without decimals
      result = result.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, valueNoDecimals);
      result = result.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
      result = result.replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, valueNoDecimalsWithSpaces);
      result = result.replace(/\{\{\s*amount_no_decimals_with_apostrophe_separator\s*\}\}/g, valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
      
      return result;
    }

    /**
     * Get sized image URL
     */
    function getSizedImageUrl(url, size) {
      if (!url) return '';
      return url.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${size}.$1`);
    }

    /**
     * Escape HTML
     */
    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    // ============================================
    // EVENT LISTENERS
    // ============================================

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    // Close buttons inside (like on empty state)
    drawer.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', closeDrawer);
    });

    // Overlay click
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });

    // Toggle buttons (using event delegation for dynamic elements)
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-cart-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        toggleDrawer();
      }
    });

    // Initial item listeners
    attachItemListeners();

    // Notes toggle
    if (notesToggle) {
      notesToggle.addEventListener('click', function() {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', !isExpanded);
      });
    }

    // Notes save (debounced)
    if (notesTextarea) {
      notesTextarea.addEventListener('input', function() {
        clearTimeout(notesSaveTimeout);
        notesSaveTimeout = setTimeout(() => {
          saveNote(this.value);
        }, 500);
      });
    }

    // Custom events for programmatic control
    window.addEventListener('cart:open', openDrawer);
    window.addEventListener('cart:close', closeDrawer);
    window.addEventListener('cart:toggle', toggleDrawer);

    // Listen for cart updates to refresh drawer
    window.addEventListener('cart:updated', () => {
      refreshCart();
    });

    // Listen for item added to auto-open drawer and refresh cart
    window.addEventListener('cart:item-added', async () => {
      // Refresh cart first, then open drawer
      await refreshCart();
      openDrawer();
    });

    // Re-initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

})();

